import { MenuItem, Tooltip } from '@mui/material';
import React from 'react';

import { tooltipOffsetProps } from '../../Components/styleConstants';
import { DATA_TEST } from '../../enums';
import { MuiSxProps } from '../../types';
import { handleTabOrEscapeToLeaveSubmenu } from '../../utils/keyboardNavUtils';

interface IContextSwitcherListItemProps {
  tooltipText: string;
  dataOg: string;
  active?: boolean;
  url?: string;
  openInNewTab?: boolean;
  children: React.ReactNode;
  styleOverrides?: MuiSxProps;
  dataTest: string;
  autoFocus?: boolean;
  firstContextSwitcherItemRef?: React.MutableRefObject<HTMLLIElement>;
  anchorEl?: HTMLDivElement;
  onClick?(): void;
  handleClose?(): void;
}

export const ContextSwitcherListItem: React.FC<IContextSwitcherListItemProps> = ({
  openInNewTab,
  url,
  tooltipText,
  children,
  onClick,
  styleOverrides,
  dataTest,
  dataOg,
  firstContextSwitcherItemRef,
  anchorEl,
  autoFocus = false,
  active = false,
  handleClose
}) => {
  return (
    <Tooltip
      data-test={DATA_TEST.context_switcher_more_item_tooltip}
      title={tooltipText}
      placement="top-start"
      disableInteractive={true}
      enterNextDelay={500}
      {...tooltipOffsetProps}
    >
      <MenuItem
        ref={autoFocus ? firstContextSwitcherItemRef : undefined}
        data-og={dataOg}
        data-test={dataTest}
        disableTouchRipple={active}
        disabled={active}
        autoFocus={autoFocus && !active}
        onClick={() => {
          if (active) return;
          if (onClick) onClick();
          if (openInNewTab && url) {
            window.open(url, '_blank');
          } else if (!openInNewTab && url) {
            window.location.href = url;
          }
          if (handleClose) {
            handleClose();
          }
        }}
        onKeyDown={(e) => {
          handleTabOrEscapeToLeaveSubmenu(e.key, anchorEl as HTMLElement);
        }}
        sx={{
          display: 'flex',
          flexGrow: 1,
          gap: 1,
          pointerEvents: active ? 'none' : 'auto',
          textWrap: 'nowrap',
          alignItems: 'center',
          padding: (theme) => `${theme.spacing(0.5)} ${theme.spacing(1)}`,
          color: active ? 'primary.main' : 'text.primary',
          height: 40,
          px: 2,
          '&.Mui-disabled': {
            opacity: active ? 1 : undefined,
            backgroundColor: active ? 'transparent' : undefined,
            color: active ? 'primary.main' : undefined
          },
          ':hover': {
            backgroundColor: active ? 'background.paper' : undefined,
            cursor: active ? 'default' : 'pointer'
          },
          ':active': {
            backgroundColor: active ? undefined : 'action.selected'
          },
          ':focus-visible': {
            backgroundColor: active ? 'transparent' : undefined,
            outline: (theme: any) => `2px solid ${theme.palette.primary.main}`
          },
          ...styleOverrides
        }}
      >
        {children}
      </MenuItem>
    </Tooltip>
  );
};
